/**
 * Wire mappers for growth (FP-1449, ADR-2).
 *
 * A mapper belongs here only when the public SDK params and the wire shape
 * genuinely differ — renamed or recased fields, curated values the caller must
 * not have to know. Where the two are already the same shape, pass the params
 * straight to `createRequest` and let its `Input` generic carry the derived
 * type; a pass-through mapper is ceremony, not a gate (ADR-2's escape hatch).
 *
 * When you do write one, enumerate every field explicitly. An identity mapper
 * (`(p) => p`) type-checks but is a weaker gate: a value of a named type is
 * assignable even when it carries keys the target no longer has, so a backend
 * rename would pass. A fresh object literal triggers excess-property checking
 * and fails.
 */
import type { CreateScratcherBody } from '@fff-web/sdk-types';
import type { RequestBody } from '@fff-web/sdk-types/contract-utils';
import type { operations } from '@fff-web/sdk-types/generated/growth';

/**
 * `POST /v1/scratchers/events`.
 *
 * Earns its place: `CreateScratcherBody` is a curated public type (it pins
 * `eventTypeId: 1`) and the wire body is create-specific, so nine of its ten
 * fields are required. The explicit literal is what makes a rename or a newly
 * required wire field a compile error here.
 *
 * The partial-update siblings need no mapper: `VirtualScratcherEventRequest` and
 * `VirtualScratcherPrizeRequest` are field-for-field identical to
 * `Partial<CreateScratcherBody>` / `Partial<CreateScratcherPrizeBody>`, so the
 * `Input` generic carries the derived type directly (ADR-2's escape hatch).
 */
export const toCreateScratcherEventBody = (
  body: CreateScratcherBody,
): RequestBody<operations['createScratcherEvent']> => ({
  description: body.description,
  endDate: body.endDate,
  eventName: body.eventName,
  eventTypeId: body.eventTypeId,
  imageUrl: body.imageUrl,
  isActive: body.isActive,
  legalCopy: body.legalCopy,
  participationCadence: body.participationCadence,
  startDate: body.startDate,
  title: body.title,
});
